import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import { Video, Mic, VideoOff } from 'lucide-react';
import videoCallPreview from '../../../../../../assets/images/graphics/video_call_preview.png';

export const CameraSetupCard: React.FC = () => {
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [selectedMic, setSelectedMic] = useState('');
  const [micLevel, setMicLevel] = useState(0);
  const [streamError, setStreamError] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const animFrameRef = useRef<number>();

  /* ── Cleanup on unmount ── */
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, []);

  const micBars = 12;

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
        textAlign: 'left',
      }}
    >
      {/* Camera preview */}
      <Box
        sx={{
          bgcolor: 'common.black',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
            aspectRatio: '16/7',
            maxHeight: 220,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(to bottom right, #111827, #000000)',
            }}
          >
            <Box
              component="img"
              src={videoCallPreview}
              alt="Professional Video Call Preview"
              sx={{
                height: '100%',
                width: 'auto',
                opacity: 1,
                objectFit: 'contain',
              }}
            />
            {/* Subtle glow effect */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(59, 130, 246, 0.1)',
                mixBlendMode: 'overlay',
                pointerEvents: 'none',
              }}
            />
          </Box>
          {streamError && (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(17, 24, 39, 0.8)',
                backdropFilter: 'blur(4px)',
                px: 2,
              }}
            >
              <VideoOff size={48} color="#9ca3af" />
              <Typography
                variant="caption"
                sx={{ color: 'grey.400', textAlign: 'center', fontSize: 11 }}
              >
                Camera selection is active,
                <br />
                but preview is hidden for privacy.
              </Typography>
            </Stack>
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{ color: 'grey.500', fontSize: 10, py: 0.5 }}
        >
          Camera Preview
        </Typography>
      </Box>

      {/* Device selectors */}
      <Grid container spacing={1.5} sx={{ p: 2 }}>
        {/* Camera Device */}
        <Grid item xs={12} sm={6}>
          <Paper
            elevation={0}
            sx={{
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200',
              p: 1.5,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: 'primary.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Video size={14} color="#2563eb" />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: 'text.primary', display: 'block', lineHeight: 1 }}
                >
                  Camera Device
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontSize: 9, mt: 0.25, display: 'block' }}
                >
                  Select your preferred webcam
                </Typography>
              </Box>
            </Stack>
            <FormControl fullWidth size="small">
              <Select
                value={selectedCamera}
                onChange={(e) => setSelectedCamera(e.target.value)}
                displayEmpty
                sx={{
                  fontSize: 11,
                  bgcolor: 'background.paper',
                  '& .MuiSelect-select': { py: 0.75, px: 1.25 },
                }}
              >
                {cameras.length === 0 ? (
                  <MenuItem value="" sx={{ fontSize: 11 }}>
                    Default Camera
                  </MenuItem>
                ) : (
                  cameras.map((c) => (
                    <MenuItem key={c.deviceId} value={c.deviceId} sx={{ fontSize: 11 }}>
                      {c.label || 'Camera'}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Paper>
        </Grid>

        {/* Microphone Device */}
        <Grid item xs={12} sm={6}>
          <Paper
            elevation={0}
            sx={{
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200',
              p: 1.5,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: 'teal.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Mic size={14} color="#0d9488" />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: 'text.primary', display: 'block', lineHeight: 1 }}
                >
                  Microphone Device
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontSize: 9, mt: 0.25, display: 'block' }}
                >
                  Select your preferred microphone
                </Typography>
              </Box>
            </Stack>
            <FormControl fullWidth size="small">
              <Select
                value={selectedMic}
                onChange={(e) => setSelectedMic(e.target.value)}
                displayEmpty
                sx={{
                  fontSize: 11,
                  bgcolor: 'background.paper',
                  '& .MuiSelect-select': { py: 0.75, px: 1.25 },
                }}
              >
                {microphones.length === 0 ? (
                  <MenuItem value="" sx={{ fontSize: 11 }}>
                    Default Mic
                  </MenuItem>
                ) : (
                  microphones.map((m) => (
                    <MenuItem key={m.deviceId} value={m.deviceId} sx={{ fontSize: 11 }}>
                      {m.label || 'Microphone'}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {/* Mic level */}
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: 9, display: 'block', mb: 0.5 }}
              >
                Microphone Level
              </Typography>
              <Stack direction="row" alignItems="flex-end" spacing={0.25} sx={{ height: 12 }}>
                {Array.from({ length: micBars }).map((_, i) => {
                  const threshold = (i / micBars) * 100;
                  const active = micLevel > threshold;
                  return (
                    <Box
                      key={i}
                      sx={{
                        flex: 1,
                        borderRadius: 0.25,
                        transition: 'all 75ms ease',
                        bgcolor: active
                          ? i < micBars * 0.5
                            ? 'primary.main'
                            : i < micBars * 0.8
                            ? 'success.main'
                            : 'error.main'
                          : 'grey.300',
                        height: `${50 + (i / micBars) * 50}%`,
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
};

